/**
 * « Anti-gaspi par lot » depuis la FICHE d'un produit — tableau de bord et
 * caisse (Stocks). Les portes d'entrée du quotidien sont la caisse et les fiches
 * produit (Philippe, 27/09/2026) : la remise se pose là où l'on regarde le
 * produit, sans détour par une page dédiée. On ferme la fenêtre et on est de
 * retour sur la fiche.
 *
 * Tous les lots en stock du produit, du plus court au plus long, avec leur date,
 * leur stock en rayon et en réserve, et leur remise.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { posApi } from '../../api/pos.api';
import { formatQty } from '../../utils/pos.utils';
import ControlesRemiseLot from './ControlesRemiseLot';

interface LotFiche {
  id: string;
  lot_number: string;
  expiry_date: string | null;
  stock_display: number;
  stock_backroom: number;
  promo_percent?: number | null;
}

function dateFr(iso: string | null): string {
  if (!iso) return 'sans date';
  const [a, m, j] = String(iso).slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

function joursRestants(iso: string | null): number | null {
  if (!iso) return null;
  const dlc = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  const auj = new Date();
  auj.setHours(0, 0, 0, 0);
  return Math.round((dlc.getTime() - auj.getTime()) / 86_400_000);
}

export default function RemiseLotsModal({
  product,
  onClose,
}: {
  product: { id: string; name: string; unit?: string | null; is_weighted?: boolean; prices?: Array<{ amount_cents: number; currency?: string }> };
  onClose: () => void;
}) {
  const [lots, setLots] = useState<LotFiche[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const prix = product.prices?.find((p) => !p.currency || p.currency === 'EUR')?.amount_cents ?? null;
  const unite = product.is_weighted || product.unit === 'kg' ? '/kg' : '';

  const charger = useCallback(async () => {
    try {
      const all: LotFiche[] = await posApi.getProductLots(product.id);
      setLots((all || []).filter((l) => Number(l.stock_display) + Number(l.stock_backroom) > 0));
    } catch {
      setErreur('Impossible de charger les lots de ce produit.');
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    void charger();
  }, [charger]);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-start justify-center p-4 pt-10">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-remise-lots"
        className="bg-white rounded-2xl w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl"
      >
        <div className="p-4 border-b border-gray-200 flex items-center gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="titre-remise-lots" className="text-lg font-bold text-gray-900 truncate">
              🏷️ Anti-gaspi — {product.name}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Remise un lot sans toucher aux autres. Au scan, la caisse demandera quel lot le client a en main.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="ml-auto text-gray-400 text-3xl leading-none px-2">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {message && <p className="text-sm font-medium text-emerald-700">{message}</p>}
          {erreur && <p className="text-sm font-medium text-red-700">{erreur}</p>}
          {loading && <p className="text-center text-gray-500 py-6">Chargement des lots…</p>}
          {!loading && lots.length === 0 && (
            <p className="text-center text-gray-500 py-6">Aucun lot en stock.</p>
          )}
          {lots.length === 1 && (
            <p className="text-xs text-gray-500">
              Un seul lot en stock : la remise ne concernera que lui, et la caisse posera la question tant qu'il en reste.
            </p>
          )}
          {lots.map((l) => {
            const j = joursRestants(l.expiry_date);
            const urgent = j != null && j <= 2;
            return (
              <div key={l.id} className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={urgent ? 'font-bold text-red-700' : 'font-semibold text-gray-900'}>
                    Date {dateFr(l.expiry_date)}
                    {j != null ? ` (${j < 0 ? 'dépassée' : j === 0 ? "aujourd'hui" : `dans ${j} j`})` : ''}
                  </span>
                  <span className="text-xs text-gray-500">lot {l.lot_number}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Rayon {formatQty(l.stock_display)} · réserve {formatQty(l.stock_backroom)}
                </div>
                <ControlesRemiseLot
                  lotId={l.id}
                  promoPercent={l.promo_percent}
                  prixCents={prix}
                  unite={unite}
                  onChange={(msg, err) => {
                    setMessage(msg);
                    setErreur(err ?? null);
                    if (msg) void charger();
                  }}
                />
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-[48px] rounded-xl bg-gray-100 text-gray-800 font-semibold active:bg-gray-200"
          >
            Retour à la fiche
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
